import { useLoadScript } from '@react-google-maps/api';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Modal, ModalBody } from 'reactstrap';

import CreateProjectDropdown from '../../components/DropdownStyle/CreateProjectDropdown';
import ButtonTextLoader from '../../components/Loaders/ButtonTextLoader';
import MapsAutocompleteDropdown from '../../components/MapsAutocompleteDropdown/MapsAutocompleteDropdown';
import ReactSelectDropdown, {
  filterOption,
} from '../../components/ReactSelectDropdown/ReactSelectDropdown';
import useAutocompleteService from '../../customHooks/usePlacesAutocompleteService';
import {
  PrimaryButton,
  RadiusButton,
  WhiteButton,
} from '../../styledComponents/buttons';
import {
  CommonFlex,
  CommonGrid,
  CommonText,
  FormAlert,
  InputField,
  InputLabel,
  InputWithLabelSection,
  RedStar,
} from '../../styledComponents/common';
import { checkHtml, checkUrl } from '../../utils/helper';
import {
  AddTaskLocationInfoContainer,
  AddTaskLocationSection,
} from '../Tasks/CreateTask/createTaskStyles';
import TaskLocationMap from '../Tasks/TaskLocationMap';

const mapLibraries = ['places'];
const mapIds = [process.env.REACT_APP_JOB_SITES_MAP_ID];

const EditTaskLocationModal = ({
  isOpen,
  toggle,
  // autocomplete and geocoding service
  editTaskLocation,
  editTaskLocationLoading,
  getOfficesShortList,
  officesShortList,
  taskLocation,
  selectedFilterOffice,
  selectedFilterRegion,
  searchTerm,
}) => {
  const { t } = useTranslation();

  const [locationName, setLocationName] = useState('');
  const [locationAddress, setLocationAddress] = useState('');
  const [radiusInput, setRadiusInput] = useState(100);
  const [saveErrors, setSaveErrors] = useState({});
  const radiusOptions = [100, 150, 200, 300];
  const [isUpdated, setIsUpdated] = useState(false);
  const [addressMarker, setAddressMarker] = useState(null);
  const [selectedOffice, setSelectedOffice] = useState(null);
  const [officeOptions, setOfficeOptions] = useState([]);
  const [selectedRegion, setSelectedRegion] = useState(null);
  const [regionOptions, setRegionOptions] = useState([]);

  // map states
  const [map, setMap] = useState(null);

  const [userCurrentLocation, setUserCurrentLocation] = useState(null);

  const { isLoaded, loadError } = useLoadScript({
    id: 'job-site-details-map-script-loader',
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_JAVASCRIPT_API_KEY,
    libraries: mapLibraries,
    mapIds,
  });

  const {
    geocoderService,
    getPlacePredictions,
    placePredictions,
    setPlacePredictions,
    isPredictionsLoading,
    refreshSessionToken,
  } = useAutocompleteService({ map, debounceTimeout: 500 });

  useEffect(() => {
    fetch('https://hutils.loxal.net/whois')
      .then(resp => resp.json())
      .then(res => {
        if (res && res.ip) {
          setUserCurrentLocation({
            lat: res.latitude,
            lng: res.longitude,
          });
        }
      })
      .catch(err => {
        console.log(err);
      });
  }, []);

  useEffect(() => {
    let list = [];
    if (officesShortList && officesShortList.length > 0) {
      officesShortList.forEach(office => {
        list.push({
          label: office.name,
          value: office.id,
        });
      });
    }
    setOfficeOptions(list);
  }, [officesShortList]);

  useEffect(() => {
    // Initialize region options
    const regions = [
      { label: 'Mina', value: 'mina' },
      { label: 'Arafat', value: 'arafat' },
    ];
    setRegionOptions(regions);
  }, []);

  // Set form data when task location is received
  useEffect(() => {
    if (taskLocation) {
      setLocationName(taskLocation.name || '');
      setLocationAddress(taskLocation.address || '');
      setRadiusInput(taskLocation.radius || 100);

      const lat = parseFloat(taskLocation.latitude) || 0;
      const lng = parseFloat(taskLocation.longitude) || 0;

      if (lat && lng) {
        setAddressMarker({ lat, lng });
        setUserCurrentLocation({ lat, lng });
      }

      // Set selected office if available
      if (taskLocation.office && officesShortList.length > 0) {
        const office = officesShortList.find(
          o => o.id === taskLocation.office.id,
        );
        if (office) {
          setSelectedOffice({
            label: office.name,
            value: office.id,
          });
        }
      }

      // Set selected region if available
      if (taskLocation.region) {
        setSelectedRegion({
          label: taskLocation.region,
          value: taskLocation.region,
        });
      }
    }
  }, [taskLocation, officesShortList]);

  useEffect(() => {
    if (map && userCurrentLocation) {
      map.panTo(userCurrentLocation);
    }
  }, [map, userCurrentLocation]);

  const handleGetGeocode = place => {
    if (geocoderService && place && place.place_id && map) {
      setLocationAddress(place.description);
      geocoderService
        .geocode({ placeId: place.place_id })
        .then(({ results }) => {
          handleSetAddressMarker(results[0].geometry.location);
        })
        .catch(e => {
          console.log(e);
        });
      setPlacePredictions([]);
    }
    refreshSessionToken();
  };

  const handleReverseGeocode = coord => {
    if (geocoderService && coord) {
      setPlacePredictions([]);
      geocoderService
        .geocode({ location: coord })
        .then(results => {
          if (results.results && results.results.length) {
            setLocationAddress(results.results[0].formatted_address);
          } else {
            setLocationAddress(`${coord.lat()}, ${coord.lng()}`);
          }
        })
        .catch(e => {
          console.log(e);
        });
    }
  };

  const handleSetAddressFromMap = location => {
    if (location && location.latLng) {
      handleSetAddressMarker(location.latLng);
      handleReverseGeocode(location.latLng);
    }
  };

  const handleSetAddressMarker = location => {
    if (location) {
      setAddressMarker(location);
      handleMapPanAndZoomToSingleLocation(location);
    }
  };

  const handleMapPanAndZoomToSingleLocation = location => {
    if (map) {
      map.panTo(location);
      map.setZoom(17);
    }
  };

  const onLocationNameChange = e => {
    const { value } = e.target;
    setLocationName(value);
    if (saveErrors && saveErrors['locationName']) {
      delete saveErrors['locationName'];
    }
  };

  const onLocationAddressChange = e => {
    const { value } = e.target;
    getPlacePredictions({ input: value });
    setLocationAddress(value);
    if (saveErrors && saveErrors['locationAddress']) {
      delete saveErrors['locationAddress'];
    }
  };

  const checkError = () => {
    let newSaveErrors = {};
    let formIsValid = true;

    if (checkUrl(locationName)) {
      formIsValid = false;
      newSaveErrors['locationName'] = 'task location name can not contain url';
    } else if (checkHtml(locationName)) {
      formIsValid = false;
      newSaveErrors['locationName'] = 'Invalid job site name';
    }

    if (checkUrl(locationAddress)) {
      formIsValid = false;
      newSaveErrors['locationAddress'] = 'Address can not contain url';
    } else if (checkHtml(locationAddress)) {
      formIsValid = false;
      newSaveErrors['locationAddress'] = 'Invalid address input';
    }

    if (!radiusInput) {
      formIsValid = false;
      newSaveErrors['radius'] = 'Radius is required';
    } else if (radiusInput % 1 !== 0) {
      formIsValid = false;
      newSaveErrors['radius'] = 'Fraction is not allowed for radius';
    } else if (radiusInput < 50) {
      formIsValid = false;
      newSaveErrors['radius'] = 'Minimum value for radius is 50';
    }

    if (!selectedOffice || !selectedOffice.value) {
      formIsValid = false;
      newSaveErrors['office'] = 'Office is required';
    }

    setSaveErrors(newSaveErrors);
    return formIsValid;
  };

  const handleEditTaskLocation = () => {
    if (checkError()) {
      const payload = {
        name: locationName,
        radius: radiusInput,
      };

      if (selectedOffice && selectedOffice.value) {
        payload.office_id = selectedOffice.value;
      }

      if (selectedRegion && selectedRegion.value) {
        payload.region = selectedRegion.value;
      }

      if (locationAddress) {
        payload.address = locationAddress;
      }

      if (addressMarker) {
        payload.latitude =
          typeof addressMarker.lat === 'function'
            ? addressMarker.lat()
            : addressMarker.lat;
        payload.longitude =
          typeof addressMarker.lng === 'function'
            ? addressMarker.lng()
            : addressMarker.lng;
      }
      editTaskLocation({
        location_id: taskLocation.id,
        ...payload,
        // Pass current filter state for refreshing list with same filters
        selectedOffice: selectedFilterOffice,
        selectedRegion: selectedFilterRegion,
        searchTerm,
      });

      setIsUpdated(true);
      toggle();
    }
  };

  const clearState = () => {
    setLocationName('');
    setLocationAddress('');
    setRadiusInput(100);
    setSaveErrors({});
    setAddressMarker(null);
    setMap(null);
    setSelectedOffice(null);
    setSelectedRegion(null);
    setUserCurrentLocation(null);
    setIsUpdated(false);
  };

  const handleToggle = () => {
    clearState();
    toggle();
  };

  useEffect(() => {
    getOfficesShortList && getOfficesShortList();
  }, []);
  return (
    <Modal
      isOpen={isOpen}
      toggle={handleToggle}
      style={{ marginTop: `2vh`, maxWidth: `950px` }}
    >
      <ModalBody style={{ padding: `0` }}>
        <AddTaskLocationSection>
          <AddTaskLocationInfoContainer>
            {' '}
            <CommonText name title fontSize="16px" margin="auto 0 10px 0">
              {t('edit_location')}
            </CommonText>
            <InputWithLabelSection margin="20px 0 0">
              <InputLabel>
                {t('location_name')} <RedStar>*</RedStar>
              </InputLabel>
              <InputField
                type="text"
                placeholder={t('type_location_name')}
                value={locationName}
                onChange={e => onLocationNameChange(e)}
                style={
                  saveErrors &&
                  saveErrors[`locationName`] && {
                    border: `1px solid #fe5969`,
                    backgroundColor: `rgba(252, 87, 104, 0.05)`,
                  }
                }
              />
              {saveErrors &&
                saveErrors['locationName'] &&
                saveErrors['locationName'] !== undefined && (
                  <FormAlert>{saveErrors['locationName']}</FormAlert>
                )}
            </InputWithLabelSection>{' '}
            <InputWithLabelSection margin="20px 0 0">
              <InputLabel>
                {t('office')} <RedStar>*</RedStar>
              </InputLabel>{' '}
              <ReactSelectDropdown
                isSearchable
                value={selectedOffice}
                options={officeOptions}
                placeholder={t('select_office')}
                onChange={e => {
                  setSelectedOffice(e);
                  if (saveErrors && saveErrors['office']) {
                    delete saveErrors['office'];
                  }
                }}
                styles={CreateProjectDropdown()}
                filterOption={filterOption}
              />
              {saveErrors &&
                saveErrors['office'] &&
                saveErrors['office'] !== undefined && (
                  <FormAlert>{saveErrors['office']}</FormAlert>
                )}
            </InputWithLabelSection>
            <InputWithLabelSection margin="20px 0 0">
              <InputLabel>{t('region')}</InputLabel>{' '}
              <ReactSelectDropdown
                isSearchable
                value={selectedRegion}
                options={regionOptions}
                placeholder={t('select_region')}
                onChange={e => {
                  setSelectedRegion(e);
                }}
                styles={CreateProjectDropdown()}
                filterOption={filterOption}
              />
            </InputWithLabelSection>
            <InputWithLabelSection
              style={{ position: 'relative' }}
              margin="20px 0 0"
            >
              <InputLabel>
                {t('location_address')} <RedStar>*</RedStar>
              </InputLabel>
              <InputField
                type="text"
                placeholder={t('type_location_address')}
                value={locationAddress}
                onChange={e => onLocationAddressChange(e)}
                style={
                  saveErrors &&
                  saveErrors[`locationAddress`] && {
                    border: `1px solid #fe5969`,
                    backgroundColor: `rgba(252, 87, 104, 0.05)`,
                  }
                }
              />
              {saveErrors &&
                saveErrors['locationAddress'] &&
                saveErrors['locationAddress'] !== undefined && (
                  <FormAlert>{saveErrors['locationAddress']}</FormAlert>
                )}
              {!isPredictionsLoading &&
              placePredictions &&
              placePredictions.length &&
              locationAddress ? (
                <MapsAutocompleteDropdown
                  dropdownOptions={placePredictions}
                  handleSelect={handleGetGeocode}
                />
              ) : null}
            </InputWithLabelSection>
            <InputWithLabelSection margin="20px 0 0">
              <CommonGrid>
                <InputLabel>
                  {t('location_radius')} <RedStar>*</RedStar>
                </InputLabel>
              </CommonGrid>
              <CommonFlex justifyContent="space-between" gap="12px">
                {radiusOptions.map((option, i) => {
                  return (
                    <RadiusButton
                      key={option}
                      active={radiusInput === option}
                      onClick={() => {
                        if (radiusInput !== option) {
                          setRadiusInput(option);
                          if (saveErrors && saveErrors['radius']) {
                            delete saveErrors['radius'];
                          }
                        }
                      }}
                    >
                      {option} m
                    </RadiusButton>
                  );
                })}
              </CommonFlex>
            </InputWithLabelSection>
            <CommonGrid
              justifyContent="unset"
              padding="10px 0 0 0"
              margin="30px 0 0"
            >
              <WhiteButton onClick={() => handleToggle()}>
                {t('cancel')}
              </WhiteButton>{' '}
              <PrimaryButton
                disabled={
                  !locationName || !locationAddress || !selectedOffice?.value
                }
                onClick={() => handleEditTaskLocation()}
              >
                {editTaskLocationLoading ? (
                  <ButtonTextLoader
                    loadingText={t('updating')}
                    fontSize="13px"
                  />
                ) : (
                  t('update')
                )}
              </PrimaryButton>
            </CommonGrid>
          </AddTaskLocationInfoContainer>
          <TaskLocationMap
            setMap={setMap}
            singleAddressMarker={addressMarker}
            setAddressFromMap={handleSetAddressFromMap}
            radius={radiusInput}
          />
        </AddTaskLocationSection>
      </ModalBody>
    </Modal>
  );
};

export default EditTaskLocationModal;
